import OrganizerDataRow from './OrganizerDataRow';
import { ItemHeaderInfoBar } from 'owa-mail-reading-pane-infobar-view';
import MailMessageBody from '../MailMessageBody';
import { observer } from 'mobx-react';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { logUsage } from 'owa-analytics';
import { AttachmentWellView } from 'owa-attachment-well-view';
import { ControlIcons } from 'owa-control-icons';
import { formatDay, getDisplayDateFromEwsDate, formatShortMonthName } from 'owa-datetime';
import { ItemActions } from 'owa-mail-reading-pane-item-actions-view';
import loadAllAttendeesForItem from 'owa-mail-reading-pane-store/lib/actions/rollUp/loadAllAttendeesForItem';
import toggleSelectItemPart from 'owa-mail-reading-pane-store/lib/actions/toggleSelectItemPart';
import ItemPartViewState from 'owa-mail-reading-pane-store/lib/store/schema/ItemPartViewState';
import getDisplayedMessageBody from 'owa-mail-reading-pane-store/lib/utils/getDisplayedMessageBody';
import { getLatestMeetingRequestNodeId } from 'owa-mail-reading-pane-store/lib/utils/rollUp/calendarRollUpUtils';
import { ClientItem, ClientMessage } from 'owa-mail-store';
import { InstrumentationContext } from 'owa-mail-store';
import mailStore from 'owa-mail-store/lib/store/Store';
import getItemRightsManagementRestrictions from 'owa-mail-store/lib/utils/getItemRightsManagementRestrictions';
import truncateCountForDataPointAggregation from 'owa-mail-store/lib/utils/truncateCountForDataPointAggregation';
import ReadOnlyCompactRecipientWell from 'owa-readonly-recipient-well/lib/components/ReadOnlyCompactRecipientWell';
import ReadOnlyRecipientViewState from 'owa-readonly-recipient-well/lib/store/schema/ReadOnlyRecipientViewState';
import AttendeeType from 'owa-service/lib/contract/AttendeeType';
import MeetingRequestMessage from 'owa-service/lib/contract/MeetingRequestMessageType';
import * as React from 'react';
import {
    MeetingDetailsTime,
    MeetingDetailsLocation,
    MeetingRequestInfo,
    MeetingRequestConflicts,
    MeetingMessageActionType,
    lazyLogMeetingMessageActionForInvite,
} from 'owa-meeting-message';

const styles = require('./CalendarDetailCard.scss');
const classNames = require('classnames/bind').bind(styles);

export interface CalendarDetailCardProps {
    item: ClientItem;
    viewState: ItemPartViewState;
    refCallback: (instance: HTMLDivElement) => any;
    isFocused: boolean;
    instrumentationContext: InstrumentationContext;
}

@observer
export default class CalendarDetailCard extends React.Component<CalendarDetailCardProps, {}> {
    componentDidMount() {
        logUsage('RPCalDetailCardRendered', [
            (this.props.item as MeetingRequestMessage).IsOrganizer,
            truncateCountForDataPointAggregation(
                this.props.viewState.calendarRollUpViewState.calendarReponseNodeIds.length
            ),
            this.props.viewState.calendarRollUpViewState.isExpanded,
        ]);
    }

    render() {
        const { item, viewState, isFocused } = this.props;
        const calendarItem = item as MeetingRequestMessage;
        const displayedMessageBody = getDisplayedMessageBody(
            calendarItem.RightsManagementLicenseData,
            calendarItem.UniqueBody
        );
        const irmRestrictions = getItemRightsManagementRestrictions(calendarItem);
        const startDate = getDisplayDateFromEwsDate(calendarItem.Start);
        const monthName = formatShortMonthName(startDate).toUpperCase();

        const latestMeetingRequestNodeId = getLatestMeetingRequestNodeId();
        const isOldInvite = viewState.conversationNodeId != latestMeetingRequestNodeId;
        const className = classNames(
            styles.calendarCardContainer,
            { isFocused: isFocused },
            { oldInvite: isOldInvite }
        );

        return viewState ? (
            <div className={className} ref={this.props.refCallback}>
                {this.renderInfoBars(item as ClientMessage, viewState)}
                <div className={styles.clickableArea} onClick={this.onCollapse}>
                    <div className={styles.calendarCardTitle}>
                        {/*TODO: Add loc string*/ 'My calendar'}
                    </div>
                    <div className={styles.calendarCardSubjectContainer}>
                        <div className={styles.calendarCardDateContainer}>
                            <div className={styles.calendarCardDateMonth}>
                                <span className={styles.calendarCardDateMonthText}>
                                    {monthName}
                                </span>
                            </div>
                            <div className={styles.calendarCardDateDay}>
                                <span className={styles.calendarCardDateDayText}>
                                    {formatDay(startDate)}
                                </span>
                            </div>
                        </div>
                        <span className={styles.calendarCardSubject}>{calendarItem.Subject}</span>
                        <div className={styles.itemHeaderDropDownButton}>
                            <ItemActions
                                viewState={this.props.viewState}
                                item={this.props.item}
                                shouldIncludeFullInfo={true}
                                instrumentationContext={this.props.instrumentationContext}
                                hideSurfaceButton={true}
                            />
                        </div>
                    </div>
                    <MeetingDetailsTime
                        item={calendarItem}
                        className={styles.detailsRow}
                        iconStyle={styles.icon}
                    />
                    <MeetingDetailsLocation
                        item={calendarItem}
                        className={styles.detailsRow}
                        iconStyle={styles.icon}
                    />
                    <MeetingRequestConflicts
                        item={calendarItem}
                        className={styles.conflictContainer}
                        logAction={this.logAction}
                    />
                    <div className={styles.separationLine} />
                    <OrganizerDataRow
                        organizer={calendarItem.Organizer}
                        isOrganizer={calendarItem.IsOrganizer}
                        message={calendarItem}
                        isOldInvite={isOldInvite}
                    />
                    {this.renderAttendees(
                        calendarItem.RequiredAttendees,
                        calendarItem.AttendeeCounts?.RequiredAttendeesCount,
                        'Required:' /*TODO: Add loc string */
                    )}
                    {this.renderAttendees(
                        calendarItem.OptionalAttendees,
                        calendarItem.AttendeeCounts?.OptionalAttendeesCount,
                        'Optional:' /*TODO: Add loc string */
                    )}
                </div>
                <div className={styles.separationLine} />
                {this.renderAttachmentWell()}
                <div className={styles.detailsRow}>
                    <Icon className={styles.icon} iconName={ControlIcons.List} />
                    <div>
                        <MailMessageBody
                            className={styles.messageBody}
                            messageBody={displayedMessageBody}
                            item={item}
                            copyAllowed={irmRestrictions.CopyAllowed}
                            printAllowed={irmRestrictions.PrintAllowed}
                            isLoading={viewState.loadingState.isLoading}
                            undoDarkMode={viewState.undoDarkMode}
                            actionableMessageCardInItemViewState={null}
                        />
                    </div>
                </div>
                <MeetingRequestInfo
                    item={item}
                    viewState={viewState.meetingRequestViewState}
                    logAction={this.logAction}
                />
            </div>
        ) : null;
    }

    private onCollapse = () => {
        toggleSelectItemPart(this.props.viewState, true /*toggleExpandedCollapsed*/);
    };

    private renderAttachmentWell(): JSX.Element {
        const { item, viewState, instrumentationContext } = this.props;

        return viewState.attachmentWell && item.HasAttachments ? (
            <div className={styles.detailsRow}>
                <Icon
                    className={classNames(styles.icon, styles.attachIcon)}
                    iconName={ControlIcons.Attach}
                />
                <AttachmentWellView
                    attachmentWellViewState={viewState.attachmentWell}
                    parentItemId={{
                        ...item.ItemId,
                        mailboxInfo: item.MailboxInfo,
                    }}
                    isDraft={false}
                    instrumentationContext={instrumentationContext}
                />
            </div>
        ) : null;
    }

    private renderAttendees(
        attendees: AttendeeType[],
        attendeeCount: number,
        label: string
    ): JSX.Element {
        const recipients = this.getMailboxList(attendees);
        return recipients.length > 0 ? (
            <div className={styles.attendeeContainer}>
                <span>{label}</span>
                <ReadOnlyCompactRecipientWell
                    className={styles.attendeeList}
                    noneTypeRecipients={recipients}
                    toRecipients={null}
                    ccRecipients={null}
                    bccRecipients={null}
                    noneTypeRecipientCount={attendeeCount}
                    hideTrailingComponentIfNoTruncation={true}
                    getAllRecipientsHandler={this.getAllAttendees}
                />
            </div>
        ) : null;
    }

    private renderInfoBars(message: ClientMessage, viewState: ItemPartViewState): JSX.Element {
        return (
            <ItemHeaderInfoBar
                message={message}
                viewState={viewState}
                instrumentationContext={this.props.instrumentationContext}
            />
        );
    }

    private getMailboxList(attendees: AttendeeType[]): ReadOnlyRecipientViewState[] {
        const result: ReadOnlyRecipientViewState[] = [];
        attendees &&
            attendees.forEach(attendee => {
                result.push({
                    email: attendee.Mailbox,
                });
            });
        return result;
    }

    private getAllAttendees = () => {
        loadAllAttendeesForItem(mailStore.items.get(this.props.item.ItemId.Id));
    };

    private logAction = (interactionType: MeetingMessageActionType, propertyNames?: string[]) => {
        lazyLogMeetingMessageActionForInvite.importAndExecute(
            interactionType,
            this.props.item,
            'CalendarRollUp',
            propertyNames
        );
    };
}
